import React, { useState } from 'react';
import { Box, Typography, Collapse, Button } from '@mui/material';
import { Heart, Mail, Footprints, Trophy, Image, Bell, ChevronDown } from 'lucide-react';
import { appStoreUrls } from '../config/environment';

const PINK = '#ED1790';
const PINK_DEEP = '#B01F8E';

const APP_LOGO_SRC = `${process.env.PUBLIC_URL}/logoNoahsArk.svg`;
const APP_STORE_BADGE_SRC = `${process.env.PUBLIC_URL}/appstore-badge.svg`;
const PLAY_STORE_BADGE_SRC = `${process.env.PUBLIC_URL}/playstore-badge.svg`;

const AppLogo = ({ size = 44 }: { size?: number }) => (
  <Box
    component="img"
    src={APP_LOGO_SRC}
    alt="Noah's Ark Pet Connect"
    sx={{
      width: size,
      height: size,
      borderRadius: '22%',
      flexShrink: 0,
      background: '#fff',
      border: '2px solid rgba(255,255,255,.85)',
      boxShadow: '0 6px 18px -8px rgba(0,0,0,.35)',
      objectFit: 'cover',
    }}
  />
);

const scrollbarSx = {
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(237, 23, 144, .35)',
    borderRadius: 999,
  },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
};

const WaveHeader = ({ compact = false }: { compact?: boolean }) => (
  <Box
    sx={{
      position: 'relative',
      background: PINK,
      pt: compact ? 2 : 2.5,
      pb: compact ? 2.75 : 3.5,
      px: 2.5,
      flexShrink: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <AppLogo size={compact ? 40 : 44} />
      <Box>
        <Typography
          sx={{
            color: '#fff',
            fontFamily: 'var(--napc-display)',
            fontWeight: 800,
            fontSize: compact ? '1.05rem' : '1.15rem',
            lineHeight: 1.1,
            letterSpacing: '-.02em',
          }}
        >
          Noah&apos;s Ark
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,.92)',
            fontFamily: 'var(--napc-text)',
            fontSize: '0.82rem',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          Pet Connect
        </Typography>
      </Box>
    </Box>
    <Box
      component="svg"
      viewBox="0 0 400 24"
      preserveAspectRatio="none"
      sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, width: '100%', height: 20 }}
    >
      <path d="M0,12 Q50,24 100,12 T200,12 T300,12 T400,12 L400,24 L0,24 Z" fill="#fff" />
    </Box>
  </Box>
);

const WaveFooter = () => (
  <Box
    sx={{
      position: 'relative',
      background: PINK,
      pt: 3,
      pb: 2.25,
      px: 2,
      textAlign: 'center',
      flexShrink: 0,
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 400 24"
      preserveAspectRatio="none"
      sx={{ position: 'absolute', left: 0, right: 0, top: -1, width: '100%', height: 20 }}
    >
      <path d="M0,12 Q50,0 100,12 T200,12 T300,12 T400,12 L400,0 L0,0 Z" fill="#fff" />
    </Box>
    <Heart className="w-4 h-4" style={{ color: '#fff', margin: '0 auto 8px' }} fill="#fff" />
    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.84rem', lineHeight: 1.35 }}>
      ¡Todo por el bienestar y felicidad de nuestras mascotas!
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,.85)', fontSize: '0.74rem', mt: 0.4, lineHeight: 1.3 }}>
      Everything for the well-being and happiness of our pets!
    </Typography>
  </Box>
);

const FeatureItem = ({ icon, es, en }: { icon: React.ReactNode; es: string; en: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: PINK,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#111827', fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.3 }}>{es}</Typography>
      <Typography sx={{ color: '#6b7280', fontSize: '0.72rem', lineHeight: 1.25, mt: 0.15 }}>{en}</Typography>
    </Box>
  </Box>
);

const StoreBadge = ({
  href,
  iconSrc,
  description,
  platform,
  ariaLabel,
}: {
  href: string;
  iconSrc: string;
  description: string;
  platform: string;
  ariaLabel: string;
}) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      flex: 1,
      minWidth: 0,
      px: 1,
      py: 0.65,
      borderRadius: 1.5,
      background: '#111827',
      color: '#fff',
      textDecoration: 'none',
      border: '1px solid rgba(255,255,255,.08)',
      transition: 'transform 0.15s, background 0.15s, box-shadow 0.15s',
      '&:hover': {
        background: '#000',
        transform: 'translateY(-1px)',
        boxShadow: '0 8px 20px -12px rgba(0,0,0,.55)',
      },
    }}
  >
    <Box
      component="img"
      src={iconSrc}
      alt=""
      sx={{ width: 26, height: 26, flexShrink: 0 }}
    />
    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
      <Typography sx={{ fontSize: '0.58rem', lineHeight: 1, opacity: 0.88, whiteSpace: 'nowrap' }}>
        {description}
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.15, mt: 0.15, whiteSpace: 'nowrap' }}>
        {platform}
      </Typography>
    </Box>
  </Box>
);

const StoreButtonsRow = () => (
  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 2, width: '100%' }}>
    <StoreBadge
      href={appStoreUrls.ios}
      iconSrc={APP_STORE_BADGE_SRC}
      description="Download on the"
      platform="App Store"
      ariaLabel="Download on the App Store"
    />
    <StoreBadge
      href={appStoreUrls.android}
      iconSrc={PLAY_STORE_BADGE_SRC}
      description="GET IT ON"
      platform="Google Play"
      ariaLabel="Get it on Google Play"
    />
  </Box>
);

const LoginTip = ({ dense = false }: { dense?: boolean }) => (
  <Box
    sx={{
      position: 'relative',
      border: '1px solid rgba(237,23,144,.2)',
      borderRadius: 2,
      background: 'rgba(237,23,144,.05)',
      px: dense ? 1.5 : 2,
      pt: dense ? 2.5 : 3,
      pb: dense ? 1.5 : 2,
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: PINK,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Mail className="w-4 h-4" style={{ color: '#fff' }} />
    </Box>
    <Typography sx={{ color: '#111827', fontSize: dense ? '0.8rem' : '0.85rem', fontWeight: 600, lineHeight: 1.35 }}>
      Ingresa con el{' '}
      <Box component="span" sx={{ color: PINK_DEEP, fontWeight: 800 }}>mismo email/teléfono</Box>{' '}
      con el que te registraste.
    </Typography>
    <Typography sx={{ color: '#6b7280', fontSize: '0.74rem', lineHeight: 1.35, mt: 0.5 }}>
      Sign in with the same email/phone you used to register.
    </Typography>
  </Box>
);

const FEATURES = [
  { icon: <Footprints className="w-3.5 h-3.5" style={{ color: '#fff' }} />, es: 'Resultados en tiempo real.', en: 'Real-time results.' },
  { icon: <Bell className="w-3.5 h-3.5" style={{ color: '#fff' }} />, es: 'Notificaciones al instante.', en: 'Instant notifications.' },
  { icon: <Image className="w-3.5 h-3.5" style={{ color: '#fff' }} />, es: 'Comparte y conecta.', en: 'Share and connect.' },
  { icon: <Trophy className="w-3.5 h-3.5" style={{ color: '#fff' }} />, es: 'Más concursos y premios.', en: 'More contests and prizes.' },
];

const DownloadAppCard: React.FC = () => {
  const [benefitsOpen, setBenefitsOpen] = useState(false);

  return (
    <>
      {/* Mobile / tablet: banner compacto arriba */}
      <Box
        sx={{
          display: { xs: 'block', lg: 'none' },
          borderRadius: 2,
          overflow: 'hidden',
          background: '#fff',
          border: '1px solid rgba(237,23,144,.18)',
          boxShadow: '0 8px 28px -18px rgba(237,23,144,.45)',
        }}
      >
        <Box sx={{ background: `linear-gradient(135deg, ${PINK} 0%, ${PINK_DEEP} 100%)`, px: 2, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <AppLogo size={40} />
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                Descarga la app
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,.9)', fontSize: '0.78rem' }}>
                Download the app · Free on iOS & Android
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: '#374151', fontSize: '0.88rem', lineHeight: 1.4, mb: 1.5 }}>
            Lleva seguimiento del concurso desde tu celular.
            <Box component="span" sx={{ display: 'block', color: '#6b7280', fontSize: '0.8rem', mt: 0.25 }}>
              Track the contest from your phone.
            </Box>
          </Typography>

          <StoreButtonsRow />

          <LoginTip dense />

          <Button
            fullWidth
            onClick={() => setBenefitsOpen((v) => !v)}
            endIcon={
              <ChevronDown
                className="w-4 h-4"
                style={{
                  transform: benefitsOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{
              mt: 1.5,
              textTransform: 'none',
              color: PINK_DEEP,
              fontWeight: 700,
              fontSize: '0.82rem',
              justifyContent: 'space-between',
            }}
          >
            {benefitsOpen ? 'Ocultar beneficios / Hide benefits' : 'Ver beneficios de la app / See app benefits'}
          </Button>

          <Collapse in={benefitsOpen}>
            <Box sx={{ pt: 1.5 }}>
              {FEATURES.map((f) => (
                <FeatureItem key={f.es} icon={f.icon} es={f.es} en={f.en} />
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* Desktop: rail izquierdo completo con scroll interno */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          background: '#fff',
          border: '1px solid rgba(237,23,144,.15)',
          boxShadow: '0 4px 24px -12px rgba(237,23,144,.35)',
          position: 'sticky',
          top: 24,
          alignSelf: 'flex-start',
          maxHeight: 'calc(100dvh - 48px)',
          ...scrollbarSx,
        }}
      >
        <WaveHeader />

        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, ...scrollbarSx }}>
          <Box sx={{ px: 2.25, py: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={{ color: '#111827', fontSize: '0.95rem' }}>No olvides</Typography>
              <Typography
                sx={{
                  color: PINK,
                  fontFamily: 'var(--napc-display)',
                  fontWeight: 800,
                  fontSize: '1.45rem',
                  lineHeight: 1.15,
                  my: 0.4,
                  letterSpacing: '-.02em',
                }}
              >
                descargar la app
              </Typography>
              <Typography sx={{ color: '#111827', fontSize: '0.9rem', lineHeight: 1.35 }}>
                para llevar seguimiento del concurso.
              </Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '0.78rem', mt: 0.5 }}>
                Don&apos;t forget to download the app to keep track of the contest.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.75,
                py: 1,
                borderRadius: 999,
                background: PINK,
                mb: 1.75,
              }}
            >
              <Heart className="w-4 h-4" style={{ color: '#fff', flexShrink: 0 }} fill="#fff" />
              <Box>
                <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                  Gratis en iOS y Android
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,.88)', fontSize: '0.7rem' }}>
                  Free on iOS and Android
                </Typography>
              </Box>
            </Box>

            <StoreButtonsRow />

            <Box sx={{ mb: 2 }}>
              <LoginTip />
            </Box>

            <Typography sx={{ color: PINK, fontWeight: 800, fontSize: '0.88rem', mb: 0.25 }}>
              Más que un concurso
            </Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '0.74rem', mb: 1.25 }}>
              More than a contest
            </Typography>

            {FEATURES.map((f) => (
              <FeatureItem key={f.es} icon={f.icon} es={f.es} en={f.en} />
            ))}
          </Box>
        </Box>

        <WaveFooter />
      </Box>
    </>
  );
};

export default DownloadAppCard;
